import { Dropdown, message, Modal } from 'antd';
import { Bell } from 'react-feather';
import AppHeaderButton from '@/components/_common/AppHeaderButton';
import NotificationsDropdownOverlay from '../NotificationsDropdownOverlay';
import { useState } from 'react';
import {
  useUserNotificationListFilter,
  useUserNotificationMarkAsRead,
} from '@/core/domain/communication/userNotification/userNotification.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import moment from 'moment';
import { getAuthCodeAndRedirect } from '@/utils/functions/getAuthCodeAndRedirect';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useNotificationHubConfig } from '@/contexts/WebsocketProvider/Notification.config';
import { useNotification } from '@/contexts/WebsocketProvider/Notification.provider';
import { UserNotificationOriginEnum } from '@/core/domain/communication/userNotification/userNotification.types';
import useChatApiTaskFeedback from '@/core/domain/chat-api/tasks/tasks.hooks';
import { ChatAPIStatus } from '@/core/domain/chat-api/tasks/tasks.types';
import { useBitrixNotification } from './useBitrixNotification';

const NotificationsDropdownMenu = () => {
  const { notificationCount } = useNotification();

  const notificationConfig = useNotificationHubConfig();

  const [isRead, setIsRead] = useState(false);
  const [generateCode, setGenerateCode] = useState(false);

  const notificationFilter = useUserNotificationListFilter({ isRead });
  const notificationMarkAsRead = useUserNotificationMarkAsRead();

  const chatApiTaskFeedback = useChatApiTaskFeedback();

  const notifications = flatInfiniteData(notificationFilter.data);

  const notificationRedirect = async (options: {
    redirectUrl: string;
    userNotificationid: number;
  }) => {
    const url = new URL(options.redirectUrl);

    await notificationMarkAsRead.mutateAsync({
      userNotificationIds: [options.userNotificationid],
    });

    if (url.host === window.location.host) {
      window.location.href = options.redirectUrl;
    } else if (!url.href.includes(process.env.REACT_APP_GP_URL || '')) {
      window.open(options.redirectUrl, '_blank');
    } else {
      setGenerateCode(true);
      await getAuthCodeAndRedirect(options.redirectUrl, '_blank', {
        destinationUrl: url.origin + '/auth-code',
      });
      setGenerateCode(false);
    }
  };

  const handleBitrixFeedback = (
    status: ChatAPIStatus,
    notificationId: number,
    metadata?: string,
    onCallback?: VoidFunction,
  ) => {
    try {
      const { taskId }: { taskId: string } = metadata
        ? JSON.parse(metadata)
        : null;

      if (!metadata || !taskId) {
        return message.error(
          'Não foi encontrado o id do chamado para envio do feedback',
          6,
        );
      }

      Modal.confirm({
        zIndex: 1100,
        title: `Resposta ao chamado #${taskId}`,
        content: (
          <>
            Você está{' '}
            <strong>{`${
              status === ChatAPIStatus.FEEDBACK_SUCCESS
                ? 'confirmando'
                : 'recusando'
            }`}</strong>{' '}
            a resolução do chamado. Deseja continuar?
          </>
        ),
        okText:
          status === ChatAPIStatus.FEEDBACK_SUCCESS
            ? 'Resolvido'
            : 'Não resolvido',
        onOk: async () => {
          await chatApiTaskFeedback.mutateAsync({ status, taskId });

          await notificationMarkAsRead.mutateAsync({
            userNotificationIds: [notificationId],
          });

          onCallback?.();
        },
      });
    } catch {}
  };

  useBitrixNotification(handleBitrixFeedback);

  return (
    <>
      <Dropdown
        placement="bottomRight"
        trigger={['click']}
        overlay={
          <NotificationsDropdownOverlay
            dataSource={notifications.map((item) => {
              const isBitrixAndActions =
                item.origin ===
                  UserNotificationOriginEnum.BITRIX_TASK_COMPLETED &&
                !!item.metadata;

              return {
                date: moment(item.createdAt).fromNow(),
                title: item.message,
                description: item.description,
                wasRead: item.isRead,
                onConfirm: () => {
                  if (isBitrixAndActions)
                    return handleBitrixFeedback(
                      ChatAPIStatus.FEEDBACK_SUCCESS,
                      item.userNotificationId,
                      item.metadata,
                    );
                  if (item.redirectUrl) {
                    notificationRedirect({
                      redirectUrl: item.redirectUrl,
                      userNotificationid: item.userNotificationId,
                    });
                  }
                },
                onCancel: isBitrixAndActions
                  ? () => {
                      return handleBitrixFeedback(
                        ChatAPIStatus.FEEDBACK_ERROR,
                        item.userNotificationId,
                        item.metadata,
                      );
                    }
                  : undefined,
                withActions: isBitrixAndActions,
                actionLabels: isBitrixAndActions
                  ? { confirm: 'Chamado resolvido', cancel: 'Não resolvido' }
                  : undefined,
              };
            })}
            isRead={isRead}
            onFilter={setIsRead}
            enableNotification={notificationConfig.balloonEnabled}
            enableAudio={notificationConfig.audioEnabled}
            toggleNotification={notificationConfig.toggleBalloon}
            toggleAudio={notificationConfig.toggleAudio}
            loadMore={notificationFilter.fetchNextPage}
            hasMore={!!notificationFilter.hasNextPage}
            isLoading={notificationFilter.isFetching}
          />
        }
      >
        <AppHeaderButton icon={<Bell />} countBadge={notificationCount} />
      </Dropdown>

      {generateCode && (
        <ModalLoadingOverlay
          messages={['gerando código de redirecionamento...']}
        />
      )}
    </>
  );
};

export default NotificationsDropdownMenu;
